import bodyImg from "../../images/bodyImg.png";
import useTheme from "../../../../common/hooks/useTheme";
import Xarrow, { Xwrapper } from "react-xarrows";
import cellImg from "../../images/cell.png";
import intersectImg from "../../images/intersect.png";
import pituitaryGlandImg from "../../images/pituitary-gland.png";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { MetricType, organConnectionsWithMetric } from "../../store/helper";
import { useDispatch, useSelector } from "react-redux";
import { platformActions, RootStatePlatform } from "../../store/platform-slice";
import HumanFigureOrgans from "./HumanFigureOrgans";
import DataPointOrgan from "./DataPointOrgan";
import { Theme } from "../../../../providers/ThemeProvider";
import ScrollableWrapper from "../common/ScrollableWrapper";
import {
  isOppositeGenderSystem,
  useClientGender,
} from "../../hooks/useClientGender";

type CircleElementProps = {
  id?: string;
  children: ReactNode;
  bottom: string;
  left?: string;
  right?: string;
  positionOfDataPoint?: "left" | "right" | "fix";
  isSelected: boolean;
  secondaryActiveConnection?: string;
};

type OrganConnectionsKey = keyof typeof organConnectionsWithMetric;

const CircleElement = ({
  id = "",
  children,
  bottom = "0px",
  left = "0px",
  isSelected,
}: CircleElementProps) => {
  const theme = useTheme();

  return (
    <div
      id={id}
      style={styles.circleContainer(theme, isSelected, bottom, left)}
    >
      {children}
    </div>
  );
};

const BodyMapTab = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [collapsedHeight, setCollapsedHeight] = useState("3em");
  const textRef = useRef<HTMLDivElement>(null);

  const selectedSystem = useSelector(
    (state: RootStatePlatform) => state.platform.selectedSystem
  );
  const gender = useClientGender();
  const selectedActiveConnection = useSelector(
    (state: RootStatePlatform) => state.platform.selectedActiveConnection
  );
  const connectionTextExpanded = useSelector(
    (state: RootStatePlatform) => state.platform.connectionTextExpanded
  );
  const reportData = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.score
  );

  useEffect(() => {
    if (textRef.current && !connectionTextExpanded) {
      const lineHeight = parseFloat(
        getComputedStyle(textRef.current).lineHeight
      );
      const twoLines = `${lineHeight * 2}px`;
      setCollapsedHeight(twoLines);
    }
  }, [connectionTextExpanded]);

  let activeConnections: { start: number; end: number[] } = {
    start: 0,
    end: [],
  };
  let connectionText;

  if (selectedSystem > 0) {
    activeConnections.start = selectedSystem;
    const curActiveConnection =
      organConnectionsWithMetric[selectedSystem as OrganConnectionsKey];

    activeConnections.end = curActiveConnection.filter(
      (item) => !isOppositeGenderSystem(item, gender)
    );
  }

  useEffect(() => {
    if (selectedSystem > 0 && selectedActiveConnection > 0) {
      connectionText = reportData.systems
        .find((system) => system.system === selectedSystem)
        ?.connectionTexts.find(
          (text) => text.id === selectedActiveConnection
        )?.text;
    }
  }, [selectedSystem, selectedActiveConnection]);

  const getConnectionText = (sys: number, activeCon: number) => {
    connectionText = reportData.systems
      .find((system) => system.system === sys)
      ?.connectionTexts.find((text) => text.id === activeCon)?.text;

    return connectionText || "No connection text available";
  };

  return (
    <ScrollableWrapper>
      <Xwrapper>
        <div
          style={styles.humanFigureContainer}
          onClick={() => {
            if (connectionTextExpanded) {
              dispatch(platformActions.setConnectionTextExpanded(false));
            }
          }}
        >
          <div style={styles.humanFigureWrapper}>
            <img src={bodyImg} alt="human figure" style={styles.mainImage} />
            {activeConnections &&
              activeConnections.end.map((connection: number, index: number) => (
                <Xarrow
                  key={index}
                  curveness={0}
                  start={MetricType[activeConnections.start]}
                  endAnchor="middle"
                  startAnchor="middle"
                  end={MetricType[connection]}
                  showHead={false}
                  strokeWidth={1}
                  color={theme.colors_ds.NEUTRAL_TEXT_DEFAULT}
                  zIndex={1}
                />
              ))}
            <HumanFigureOrgans />

            <CircleElement
              bottom={"440px"}
              left={"410px"}
              positionOfDataPoint="left"
              isSelected={
                selectedSystem === 11 ||
                selectedSystem === 12 ||
                selectedSystem === 15 ||
                selectedSystem === 17 ||
                selectedActiveConnection === 11 ||
                selectedActiveConnection === 12 ||
                selectedActiveConnection === 15 ||
                selectedActiveConnection === 17
              }
            >
              <img
                src={pituitaryGlandImg}
                alt="pituitary gland"
                style={styles.circleImg}
              />
              <div
                id={"pituitary_gland"}
                style={{
                  ...styles.extraDataPoint,
                  top: "42px",
                  left: "10px",
                }}
              />
              <DataPointOrgan
                id={11}
                top={"6px"}
                left={"23px"}
                position="fix"
              />
              <DataPointOrgan
                id={12}
                top={"6px"}
                left={"23px"}
                position="fix"
              />
              <DataPointOrgan id={15} top={"50%"} left={"0"} position="fix" />

              <DataPointOrgan
                id={17}
                top={"87px"}
                left={"23px"}
                position="fix"
              />
              <div
                id={"pituitary_gland_1"}
                style={{
                  ...styles.extraDataPoint,
                  top: "20px",
                  left: "7px",
                }}
              />
            </CircleElement>
            <CircleElement
              bottom={"294px"}
              left={"410px"}
              positionOfDataPoint="fix"
              isSelected={
                selectedSystem === 3 ||
                selectedSystem === 4 ||
                selectedSystem === 8 ||
                selectedSystem === 9 ||
                selectedSystem === 16 ||
                selectedSystem === 23 ||
                selectedActiveConnection === 3 ||
                selectedActiveConnection === 4 ||
                selectedActiveConnection === 8 ||
                selectedActiveConnection === 9 ||
                selectedActiveConnection === 16 ||
                selectedActiveConnection === 23
              }
            >
              <div
                id={"cell_image"}
                style={{
                  ...styles.extraDataPoint,
                  top: "100%",
                  left: "50%",
                }}
              />
              <img src={cellImg} alt="cell" style={styles.circleImg} />
              <DataPointOrgan id={8} top={"8px"} left={"20px"} position="fix" />
              <DataPointOrgan
                id={16}
                top={"8px"}
                left={"74px"}
                position="fix"
              />
              <DataPointOrgan id={3} top={"50%"} left={"0"} position="fix" />
              <DataPointOrgan id={4} top={"50%"} left={"100%"} position="fix" />
              <DataPointOrgan
                id={9}
                top={"85px"}
                left={"20px"}
                position="fix"
              />

              <DataPointOrgan
                id={23}
                top={"85px"}
                left={"74px"}
                position="fix"
              />
            </CircleElement>
            <CircleElement
              bottom={"140px"}
              left={"410px"}
              positionOfDataPoint="left"
              isSelected={
                selectedSystem === 24 ||
                selectedSystem === 25 ||
                selectedActiveConnection === 24 ||
                selectedActiveConnection === 25
              }
            >
              <div
                id={"muscle_zoom_image_1"}
                style={{
                  ...styles.extraDataPoint,
                  top: "3px",
                  left: "32px",
                }}
              />
              <div
                id={"muscle_zoom_image_2"}
                style={{
                  ...styles.extraDataPoint,
                  top: "3px",
                  left: "60px",
                }}
              />
              <div
                id={"muscle_zoom_image_3"}
                style={{
                  ...styles.extraDataPoint,
                  top: "50px",
                  left: "0px",
                }}
              />
              <img
                src={intersectImg}
                alt="intersect"
                style={styles.circleImg}
              />
              <DataPointOrgan
                id={25}
                top={"8px"}
                left={"20px"}
                position="fix"
              />
              <DataPointOrgan
                id={24}
                top={"85px"}
                left={"20px"}
                position="fix"
              />
            </CircleElement>

            {selectedSystem > 0 && selectedActiveConnection > 0 && (
              <div style={styles.connectionTextWrapper(theme)}>
                <div
                  ref={textRef}
                  style={{
                    overflow: "hidden",
                    maxHeight: connectionTextExpanded
                      ? "none"
                      : collapsedHeight,
                    transition: "max-height 0.3s ease",
                  }}
                >
                  {getConnectionText(selectedSystem, selectedActiveConnection)}
                </div>
                <div
                  onClick={() =>
                    dispatch(
                      platformActions.setConnectionTextExpanded(
                        !connectionTextExpanded
                      )
                    )
                  }
                  style={styles.buttonText(theme)}
                >
                  {connectionTextExpanded ? "Show less" : "Learn more"}
                </div>
              </div>
            )}
            <Xarrow
              curveness={0}
              start={"brain_image_1"}
              endAnchor="top"
              startAnchor="right"
              end={"pituitary_gland_1"}
              showHead={false}
              strokeWidth={1}
              color={theme.colors_ds.NEUTRAL_BORDER_DEFAULT}
              zIndex={19}
            />
            <Xarrow
              curveness={0}
              start={"cell_image"}
              endAnchor="top"
              startAnchor="middle"
              end={"muscle_zoom_image_1"}
              showHead={false}
              strokeWidth={1}
              color={theme.colors_ds.NEUTRAL_BORDER_DEFAULT}
              zIndex={1}
            />
            <Xarrow
              curveness={0}
              start={"cell_image"}
              endAnchor="top"
              startAnchor="middle"
              end={"muscle_zoom_image_2"}
              showHead={false}
              strokeWidth={1}
              color={theme.colors_ds.NEUTRAL_BORDER_DEFAULT}
              zIndex={1}
            />
            <Xarrow
              curveness={0}
              start={"muscle_image"}
              endAnchor="top"
              startAnchor="middle"
              end={"muscle_zoom_image_3"}
              showHead={false}
              strokeWidth={1}
              color={theme.colors_ds.NEUTRAL_BORDER_DEFAULT}
              zIndex={1}
            />
          </div>
        </div>
      </Xwrapper>
    </ScrollableWrapper>
  );
};

export default BodyMapTab;

const styles = {
  humanFigureWrapper: {
    position: "relative" as "relative",
    height: "100%",
    display: "flex",
    alignItems: "flex-end",
  },
  humanFigureContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    paddingTop: "32px",
    flex: 1,
    width: "490px",
    margin: "0 auto",
  },
  mainImage: {
    width: "100%",
  },
  circleContainer: (
    theme: Theme,
    isSelected: boolean,
    bottom: string,
    left: string
  ) => ({
    width: "94px",
    height: "94px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    borderRadius: "50%",
    border: `1px solid ${
      isSelected
        ? theme.colors_ds.NEUTRAL_TEXT_DEFAULT
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    boxShadow: isSelected ? "var(--box-shadow-general)" : "",
    position: "absolute" as "absolute",
    bottom: bottom,
    left: left,
  }),
  circleImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as "cover",
  },
  connectionTextWrapper: (theme: Theme) => ({
    width: "95%",
    position: "absolute" as "absolute",
    bottom: "0",
    left: "52%",
    transform: "translateX(-50%)",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    padding: "12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    marginBottom: "16px",
    boxShadow: "var(--box-shadow-general)",
    zIndex: 190,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  }),
  extraDataPoint: {
    position: "absolute" as "absolute",
    top: "0",
    left: "0",
  },
  buttonText: (theme: Theme) => ({
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    textAlign: "right" as "right",
    cursor: "pointer",
  }),
};
